import { Logger } from '@saviynt/common';

import { type EnvConfig } from '../types';

export const checkEnvironment = (): EnvConfig =>
  (window as { envConfig?: EnvConfig }).envConfig ?? {};

export const getLogoEndpointUrl = (logoName: string | undefined) => {
  if (!logoName) {
    return undefined;
  }

  const { IDWMS_API_URL } = checkEnvironment();

  if (IDWMS_API_URL) {
    if (logoName.endsWith('.svg')) {
      return `${IDWMS_API_URL}/resource/image/app/${logoName}`;
    }

    return `${IDWMS_API_URL}/resource/image/app/${logoName}.svg`;
  }

  return undefined;
};

export const generateRandomString = (length: number) => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  let result = '';

  for (let i = 0; i < length; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return result;
};

const formatErrorString = (args: unknown[]): string =>
  args.map((arg) => String(arg)).join(' ');

export const suppressConsoleWarningsForTests = () => {
  // Store the original console.error
  // eslint-disable-next-line no-console -- to suppress console warning
  const originalConsoleError = console.error.bind(console);

  beforeAll(() => {
    // eslint-disable-next-line no-console -- to suppress console warning
    console.error = (...args: unknown[]) => {
      const errorMessage = formatErrorString(args);

      // Add all warning checks here
      const isDesiredError = Boolean(
        typeof errorMessage === 'string' &&
          (errorMessage.includes('validateDOMNesting') ||
            errorMessage.includes('Failed prop type') ||
            errorMessage.includes('MISSING_TRANSLATION') ||
            errorMessage.includes('test was not wrapped in act'))
      );

      if (isDesiredError) {
        return;
      }

      originalConsoleError(...args);
    };
  });

  afterAll(() => {
    // eslint-disable-next-line no-console -- to suppress console warning
    console.error = originalConsoleError;
  });
};

export const getLocalizationStats = (
  ApiResponse: Record<string, string>,
  currentList: Record<string, string>
) => {
  const modifiedCurrentList = { ...currentList };
  // Collecting all the unique messages and keys as array from API response
  const modifiedApiResponse = Object.entries(ApiResponse).reduce<
    Record<string, string[]>
  >((acc, [key, message]) => {
    if (message in acc) {
      acc[message] = [...acc[message], key];

      return acc;
    }

    acc[message] = [key];

    return acc;
  }, {});

  // Messages that already exist in API response
  const keysFromCurrentListExistInApiResponse = Object.keys(modifiedCurrentList)
    // Messages that already exist in API response
    .filter((message) => {
      if (message in modifiedApiResponse) {
        return true;
      }

      return false;
    })
    // Mapping if key is correct
    .map((message) => {
      if (modifiedApiResponse[message].length === 1) {
        return {
          message,
          currentListLKey: modifiedCurrentList[message],
          apiResponseKey: modifiedApiResponse[message],
          keyNeededToMap: modifiedApiResponse[message][0],
          isCurrentKeyMatching: Boolean(
            modifiedApiResponse[message][0] === modifiedCurrentList[message]
          ),
        };
      }

      const neoKey = modifiedApiResponse[message].find((key) =>
        key.includes('savicopilot')
      );

      return {
        message,
        currentListLKey: modifiedCurrentList[message],
        apiResponseKey: modifiedApiResponse[message],
        keyNeededToMap: neoKey,
        isCurrentKeyMatching:
          Boolean(neoKey === modifiedCurrentList[message]) ||
          modifiedApiResponse[message].includes(modifiedCurrentList[message]),
      };
    })
    // Getting all the keys that are not matched correctly
    .filter((obj) => !obj.isCurrentKeyMatching);

  // Matching the correct keys from API response
  keysFromCurrentListExistInApiResponse.forEach((obj) => {
    const sodKey = obj.apiResponseKey.find((key) => key.includes('sod'));
    const labelKey = obj.apiResponseKey.find((key) => key.includes('label'));
    const certsKey = obj.apiResponseKey.find((key) => key.includes('CERTS'));

    if (sodKey) {
      modifiedCurrentList[obj.message] = sodKey;
    } else if (labelKey) {
      modifiedCurrentList[obj.message] = labelKey;
    } else if (certsKey) {
      modifiedCurrentList[obj.message] = certsKey;
    } else {
      const [option] = obj.apiResponseKey[0];

      modifiedCurrentList[obj.message] = option;
    }
  });

  // Messages that are not coming from API
  const keysFromCurrentListNeedToBeAddediInDB = Object.keys(modifiedCurrentList)
    .filter((message) => {
      if (message in modifiedApiResponse) {
        return false;
      }

      return true;
    })
    .reduce<Record<string, string>>((acc, message) => {
      acc[message] = modifiedCurrentList[message];

      return acc;
    }, {});

  const keysChangedInCurrentList = Object.keys(currentList).reduce<
    Record<string, { current: string; updateTo: string }>
  >((diff, message) => {
    if (currentList[message] !== modifiedCurrentList[message]) {
      diff[message] = {
        current: currentList[message],
        updateTo: modifiedCurrentList[message],
      };
    }

    return diff;
  }, {});

  if (Object.keys(keysChangedInCurrentList).length > 0) {
    Logger.log('No keys are pending to be added in DB');

    return;
  }

  Logger.log('Localization Stats', {
    ...(Object.keys(keysChangedInCurrentList).length > 0 && {
      updated_invertedMessages_as_per_retrieveMessages: modifiedCurrentList,
      changes_made_from_current_invertedMessages: keysChangedInCurrentList,
    }),
    key_value_pair_need_to_be_added_in_DB_for_localization:
      keysFromCurrentListNeedToBeAddediInDB,
  });

  // ▣ ★ ⚠
};
